<template>
  <section class="page-content--bgf7">
    <section class="statistic statistic2">
      <div class="container">
        <h1 class="h3">
          <v-btn
            text
            icon
            class="m-0"
            @click="$router.back()"
          >
            <i class="zmdi zmdi-long-arrow-left" />
          </v-btn>
          My New Channel
        </h1>
        <div class="row">
          <div class="col-md-12 p-b-0">
            <v-stepper
              v-model="step"
              class="primary"
              dark
            >
              <v-stepper-header>
                <v-stepper-step
                  step="1"
                  :complete="step > 1"
                >
                  General settings
                </v-stepper-step>
                <v-divider />
                <v-stepper-step
                  step="2"
                  :complete="step > 2"
                >
                  Layout
                </v-stepper-step>
                <v-divider />
                <v-stepper-step
                  :complete="step > 3"
                  step="3"
                >
                  Layout settings
                </v-stepper-step>
                <v-divider />
                <v-stepper-step
                  step="4"
                  :complete="step === 4"
                >
                  Stream options
                </v-stepper-step>
              </v-stepper-header>
            </v-stepper>
          </div>
        </div>

        <v-card
          class="m-b-40"
          :loading="loading"
        >
          <div
            v-if="step === 1"
            class="row p-t-20 p-l-20 p-r-40"
          >
            <table class="table table-borderless">
              <tbody>
                <tr>
                  <td style="width: 280px;">
                    <v-img
                      width="200"
                      height="200"
                      contain
                      class="grey darken-4 m-b-20 m-l-30"
                      :src="channel.logo ? channel.logo : defaultIcon"
                    />
                    <v-file-input
                      v-model="iconFile"
                      class="pointer"
                      placeholder="Upload a logo* (JPEG, PNG)"
                      truncate-length="15"
                      label="Channel logo*"
                      style="width: 230px;"
                      show-size
                      accept="image/png, image/jpeg"
                      prepend-icon="mdi-paperclip"
                      @change="fileUpload('logo', iconFile)"
                    />
                  </td>
                  <td>
                    <v-text-field
                      v-model="channel.name"
                      label="Channel name*"
                      :counter="255"
                      clearable
                      :rules="rules.nameRules"
                      required
                    />
                    <v-textarea
                      v-model="channel.description"
                      append-icon="mdi-comment-text"
                      label="Channel description"
                      rows="2"
                      auto-grow
                      counter
                    />
                    <v-combobox
                      v-model="channel.timezone"
                      append-icon="zmdi-time-interval"
                      item-text="name"
                      :items="timezones"
                      label="Channel timezone"
                      return-object
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div
            v-if="step === 2"
            class="row p-t-10 p-l-40 p-r-40"
          >
            <v-list-item
              three-line
              class="m-b-10"
            >
              <v-list-item-content>
                <v-list-item-title class="text-h5 mb-1">
                  Guide
                </v-list-item-title>
                <v-list-item-subtitle>
                  Channel layout consists of various elements to help you customize
                  their appearance and make the channel truly yours.
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <div class="col-md-12 p-t-0">
              <table class="table table-bordered guide-table">
                <tbody>
                  <tr>
                    <td style="width: 90px;">
                      <img src="/images/layouts/guide_bumper.png">
                    </td>
                    <td>
                      Bumper. Shows your main logo and description.
                      You can customize the bumper at the next step.
                    </td>
                    <td style="width: 90px;">
                      <img src="/images/layouts/guide_ads.png">
                    </td>
                    <td>
                      Advertising insertion. Ads can be uploaded to the system directly or
                      dynamically inserted following SCTE-35 markers by SSAI integration.
                    </td>
                    <td style="width: 90px;">
                      <img src="/images/layouts/guide_asset.png">
                    </td>
                    <td>
                      Asset. It's a movies/series and any other type of the content you like to
                      show at your channel.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div
            v-if="step === 2"
            class="row p-l-40 p-r-40"
          >
            <div class="col-md-12 p-t-0">
              <v-row no-gutters>
                <v-radio-group
                  v-model="layout"
                  @change="setLayout()"
                >
                  <v-radio
                    v-for="l in layouts"
                    :key="l.id"
                    class="p-t-0 p-b-0 m-t-0 m-b-0"
                    :value="l.extId"
                    :disabled="!l.isActive"
                  >
                    <template v-slot:label>
                      <table class="table table-borderless layout-table">
                        <tbody>
                          <tr
                            v-ripple
                            :class="{ 'inactive': !l.isActive }"
                          >
                            <td style="width: 500px;">
                              <p
                                class="text-h5 m-b-10"
                                :class="{ 'primary--text': l.extId === layout }"
                              >
                                {{ l.name }}
                              </p>
                              {{ l.description }}
                            </td>
                            <td>
                              <img
                                v-if="l.pictureURL"
                                :src="l.pictureURL"
                              >
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </template>
                  </v-radio>
                </v-radio-group>
              </v-row>
            </div>
          </div>

          <div
            v-if="step === 3"
            class="row p-l-40 p-r-40"
          >
            <div class="col-md-12">
              <v-card flat>
                <v-card-text>
                  <p class="text-overline">
                    Customize the layout of your channel
                  </p>
                  <div
                    style="background-color: #E3F2FD"
                    class="p-l-20 p-r-20"
                  >
                    <p class="text-h5 primary--text p-t-10">
                      <i class="fas fa-layer-group p-r-10" />{{ channel.layout.name }}
                    </p>
                    <img
                      v-if="channel.layout.pictureURL"
                      :src="channel.layout.pictureURL"
                    >
                    <div class="m-t-10 text-body-1 p-b-10">
                      {{ channel.layout.description }}
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </div>
            <div
              v-if="layout === layoutType.STARTER"
              class="col-md-12 p-l-30"
            >
              No additional settings required
            </div>
            <div
              v-if="[layoutType.AD, layoutType.BUMPER_AD].includes(layout)"
              class="col-md-12"
            >
              <p class="text-h5 p-l-15 p-b-0 m-b-0">
                <i class="mdi mdi-arrow-expand-horizontal p-r-10" />Minimum interval between ads
              </p>
              <v-text-field
                v-model="channel.minIntBetweenAds"
                class="p-l-15"
                label="Duration in seconds"
              />
              <p class="text-h5 p-l-15 p-b-0 m-b-0">
                <i class="mdi mdi-clock p-r-10" />Ad-block duration
              </p>
              <v-text-field
                v-model="channel.adDuration"
                class="p-l-15"
                label="Duration in seconds"
              />
            </div>
            <div
              v-if="[layoutType.BUMPER, layoutType.BUMPER_AD].includes(layout)"
              class="col-md-12"
            >
              <v-item-group mandatory>
                <p class="text-h5 p-l-15">
                  <i class="fas fa-film p-r-10" />Select bumper template
                </p>
                <v-row>
                  <v-slide-group
                    v-model="selectedBumper"
                    mandatory
                    show-arrows
                  >
                    <v-slide-item
                      v-for="bumper in bumpers"
                      :key="bumper.id"
                      v-slot="{ active, toggle }"
                    >
                      <v-card
                        class="d-flex align-center"
                        dark
                        width="372"
                        @click="toggle"
                      >
                        <v-scroll-y-transition>
                          <div
                            class="flex-grow-1 text-center"
                          >
                            <v-hover v-slot="{ hover }">
                              <v-card
                                :elevation="hover ? 12 : 2"
                                :class="{ 'on-hover': hover }"
                              >
                                <v-img
                                  :class="{ 'selected-intro' : active }"
                                  width="372"
                                  :src="bumper.pictureURL"
                                >
                                  <div
                                    v-if="hover"
                                    class="v-card--reveal"
                                    style="height: 100%;"
                                  >
                                    <v-btn
                                      color="primary"
                                      dark
                                      @click="playStreamByURL(bumper.streamURL,
                                                              'Bumper: ' + bumper.name, '', true)"
                                    >
                                      <i class="fas fa-play p-r-5" />
                                      {{ bumper.name }}
                                    </v-btn>
                                  </div>
                                </v-img>
                              </v-card>
                            </v-hover>
                          </div>
                        </v-scroll-y-transition>
                      </v-card>
                    </v-slide-item>
                  </v-slide-group>
                </v-row>
                <v-row class="p-l-15 p-t-20">
                  <v-col
                    cols="12"
                    sm="6"
                    md="6"
                    class="p-l-40"
                  >
                    <v-img
                      width="150"
                      height="150"
                      contain
                      class="grey darken-4 m-b-20"
                      :src="channel.bumperPicture ? channel.bumperPicture : channel.logo"
                    />
                    <v-file-input
                      v-model="bumperFile"
                      placeholder="Pick a bumper logo (PNG)"
                      class="pointer"
                      truncate-length="30"
                      show-size
                      accept="image/png"
                      prepend-icon="mdi-camera"
                      @change="fileUpload('bumperPicture', bumperFile)"
                    />
                    <span class="text-body-2">
                      By default, the logo uploaded on Step One will be applied
                    </span>
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                    md="6"
                    style="max-width: 470px"
                  >
                    <p class="text-h5">
                      Tag line:
                    </p>
                    <v-text-field
                      v-model="channel.bumperTagLine"
                      placeholder="Enjoy the next level of TV"
                    />
                  </v-col>
                </v-row>
              </v-item-group>
            </div>
            <div
              v-if="[layoutType.ANNOUNCE, layoutType.ANNOUNCE_AD].includes(layout)"
              class="col-md-12 m-t-10"
            >
              <v-item-group mandatory>
                <p class="text-h5 p-l-15 p-b-0 m-b-0">
                  <i class="fas fa-film p-r-10" />Select announcement template
                </p>
                <v-row class="p-l-15 p-r-15">
                  <v-col
                    v-for="announcement in announcements"
                    :key="announcement.id"
                    cols="12"
                    md="4"
                  >
                    <v-item v-slot:default="{ active, toggle }">
                      <v-card
                        class="d-flex align-center"
                        dark
                        width="372"
                        @click="toggle"
                      >
                        <v-scroll-y-transition>
                          <div
                            class="flex-grow-1 text-center"
                          >
                            <v-hover v-slot="{ hover }">
                              <v-card
                                :elevation="hover ? 12 : 2"
                                :class="{ 'on-hover': hover }"
                              >
                                <v-img
                                  :class="{ 'selected-announcement' : active }"
                                  :src="announcement.pictureURL"
                                  width="372"
                                >
                                  <div
                                    v-if="hover"
                                    class="v-card--reveal"
                                    style="height: 100%;"
                                  >
                                    <v-btn
                                      color="primary"
                                      dark
                                      @click="playStreamByURL(announcement.streamURL, 'Announcement: ' + announcement.name, '', true)"
                                    >
                                      <i class="fas fa-play p-r-5" />
                                      {{ announcement.name }}
                                    </v-btn>
                                  </div>
                                </v-img>
                              </v-card>
                            </v-hover>
                          </div>
                        </v-scroll-y-transition>
                      </v-card>
                    </v-item>
                  </v-col>
                </v-row>
              </v-item-group>
            </div>
          </div>

          <div
            v-if="step === 4"
            class="row p-t-30 p-l-40 p-r-40"
          >
            <div class="col-md-12">
              <v-item-group mandatory>
                <p class="text-h5 p-l-15 m-b-0">
                  <i class="fas fa-film p-r-10" />Select blackout template
                </p>
                <v-row class="p-l-15 p-r-15">
                  <v-col
                    v-for="blackout in blackouts"
                    :key="blackout.id"
                    cols="12"
                    md="4"
                  >
                    <v-item v-slot:default="{ active, toggle }">
                      <v-card
                        class="d-flex align-center"
                        dark
                        width="372"
                        @click="toggle"
                      >
                        <v-scroll-y-transition>
                          <div
                            class="flex-grow-1 text-center"
                          >
                            <v-hover v-slot="{ hover }">
                              <v-card
                                :elevation="hover ? 12 : 2"
                                :class="{ 'on-hover': hover }"
                              >
                                <v-img
                                  :class="{ 'selected-announcement' : active }"
                                  :src="blackout.pictureURL"
                                  width="372"
                                >
                                  <div
                                    v-if="hover"
                                    class="v-card--reveal"
                                    style="height: 100%;"
                                  >
                                    <v-btn
                                      color="primary"
                                      dark
                                      @click="playStreamByURL(blackout.streamURL, 'Blackout: ' + blackout.name, '')"
                                    >
                                      <i class="fas fa-play p-r-5" />
                                      {{ blackout.name }}
                                    </v-btn>
                                  </div>
                                </v-img>
                              </v-card>
                            </v-hover>
                          </div>
                        </v-scroll-y-transition>
                      </v-card>
                    </v-item>
                  </v-col>
                </v-row>
              </v-item-group>
            </div>
            <div class="col-md-12 watermark p-t-20">
              <p class="text-h5 p-l-15 m-b-0">
                <i class="mdi mdi-watermark p-r-10" />Channel Bug
              </p>
              <v-row class="p-l-15">
                <v-col
                  cols="12"
                  sm="12"
                  md="12"
                  class="p-b-0 p-t-0"
                >
                  <v-switch
                    v-model="channel.watermark.enabled"
                    color="success"
                    inset
                  >
                    <template v-slot:label>
                      <v-list-item v-ripple>
                        <v-list-item-content>
                          <v-list-item-title>Enable</v-list-item-title>
                          <v-list-item-subtitle>
                            A bug's main objective is to brand your video program with an identity.
                            This helps viewers to quickly identify what network is running a
                            particular program.
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-switch>
                </v-col>
              </v-row>
              <v-row
                v-if="channel.watermark.enabled"
                no-gutters
                class="p-l-15"
              >
                <v-col class="p-t-0">
                  <v-img
                    width="150"
                    height="150"
                    contain
                    class="grey darken-4 m-b-10"
                    :src="channel.watermarkPicture ? channel.watermarkPicture : channel.logo"
                  />
                  <v-file-input
                    v-model="watermarkFile"
                    placeholder="Pick a bug* (PNG)"
                    style="width: 145px;"
                    class="pointer"
                    truncate-length="30"
                    show-size
                    accept="image/png"
                    prepend-icon="mdi-camera"
                    @change="fileUpload('watermarkPicture', watermarkFile)"
                  />
                  <v-col
                    cols="12"
                    sm="6"
                    md="12"
                    class="p-t-40"
                  >
                    <v-switch
                      v-model="watermarkPreviewCycle"
                      label="Cycle Slides"
                      inset
                    />
                  </v-col>
                </v-col>
                <v-col class="p-t-0 justify-center">
                  <v-carousel
                    :cycle="watermarkPreviewCycle"
                    style="width: 640px !important; height: 360px !important;"
                    hide-delimiter-background
                    show-arrows-on-hover
                  >
                    <v-carousel-item
                      v-for="(backgroundURI, i) in watermarkBackgrounds"
                      :key="i"
                      style="width: 640px !important; height: 360px !important;"
                    >
                      <v-row
                        align="center"
                        justify="center"
                      >
                        <v-img
                          style="width: 640px !important; height: 360px !important;"
                          :src="backgroundURI"
                        >
                          <v-card-title
                            class="white--text"
                            :class="`watermark-position-${channel.watermark.position}`"
                          >
                            <v-avatar
                              v-if="!channel.watermarkPicture && !channel.logo"
                              :class="`avatar-size-${channel.watermark.size}`"
                              style="margin: 10px 18px;"
                              color="primary"
                            />
                            <img
                              v-if="channel.watermarkPicture || channel.logo"
                              :class="[`watermark-size-${channel.watermark.size}`]"
                              style="margin: 10px 20px;"
                              :src="channel.watermarkPicture || channel.logo"
                            >
                          </v-card-title>
                        </v-img>
                      </v-row>
                    </v-carousel-item>
                  </v-carousel>
                </v-col>
                <v-col class="p-t-0">
                  <h4 class="h6 m-l-50">
                    Position:
                  </h4>
                  <v-radio-group
                    v-model="channel.watermark.position"
                    class="radios m-t-5 p-l-50"
                  >
                    <v-radio
                      label="Top left"
                      value="topLeft"
                    />
                    <v-radio
                      label="Top center"
                      value="topCenter"
                    />
                    <v-radio
                      label="Top right"
                      value="topRight"
                    />
                    <v-radio
                      label="Bottom left"
                      value="bottomLeft"
                    />
                    <v-radio
                      label="Bottom center"
                      value="bottomCenter"
                    />
                    <v-radio
                      label="Bottom right"
                      value="bottomRight"
                    />
                  </v-radio-group>
                  <h4 class="h6 m-l-50">
                    Size of longest side:
                  </h4>
                  <v-radio-group
                    v-model="channel.watermark.size"
                    class="radios m-t-5 p-l-50"
                  >
                    <v-radio
                      label="200 pixels"
                      value="200"
                    />
                    <v-radio
                      label="150 pixels"
                      value="150"
                    />
                    <v-radio
                      label="100 pixels"
                      value="100"
                    />
                  </v-radio-group>
                </v-col>
              </v-row>
              <v-row
                v-if="channel.watermark.enabled"
                class="p-l-15"
              >
                <v-col
                  cols="12"
                  sm="6"
                  md="6"
                  class="text-body-2"
                >
                  Bugs typically are semi-transparent and small in size.
                  For HD content the bug size shall be around 200×200 pixels and shall be
                  uploaded in PNG format. Bugs can be animated and can flip, rotate, spin or
                  otherwise have any moving properties. In this case you have to upload an APNG
                  file.
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                  md="6"
                  class="text-body-2"
                >
                  Choose subtle coloring, unless, of course, the color is central to the brand.
                  A good bug should still be easily identified. Consider adding an outer glow
                  or drop shadow to the graphic to help it slightly stand out from the video
                  program. If you choose to add either of these styles you'll need to make
                  sure you preserve the alpha channel.
                </v-col>
              </v-row>
            </div>
          </div>

          <v-card-actions class="m-r-30 p-b-20">
            <v-spacer />
            <v-btn
              v-if="step > 1"
              large
              color="primary"
              @click="prevStep()"
            >
              ←&nbsp;Prev
            </v-btn>
            <v-btn
              v-if="step !== 4"
              large
              :disabled="!canNext"
              color="primary"
              @click="nextStep()"
            >
              Next&nbsp;→
            </v-btn>
            <v-btn
              v-if="step === 4"
              large
              :loading="isSaving"
              :disabled="!isFormValid || isSaving"
              color="success"
              @click="save()"
            >
              Create
            </v-btn>
          </v-card-actions>
        </v-card>
      </div>
    </section>

    <v-bottom-sheet
      v-model="openPlayer"
      max-width="960"
      :persistent="false"
    >
      <v-card tile>
        <iframe
          id="serviceFrameSend"
          :src="frameSrc"
          width="100%"
          height="380"
          frameborder="0"
        />
        <v-list>
          <v-list-item>
            <v-list-item-content>
              <v-list-item-title>{{ player.assetName }}</v-list-item-title>
              <v-list-item-subtitle>
                {{ player.assetDescription }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
        <v-card-actions>
          <v-spacer />
          <v-btn
            depressed
            @click="closeStream()"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-bottom-sheet>
  </section>
</template>

<script>
  import Vue from 'vue'
  import {
    processLayout,
    processTimezone,
    processTemplate,
    channelToJsonAPIChannelRequest,
  } from '@/store/converters'
  import { TEMPLATE_TYPE, LAYOUT_TYPE } from '@/shared/types'

  export default {
    data () {
      return {
        step: 1,
        channel: {
          logo: '',
          logoId: 0,
          name: '',
          description: '',
          timezone: {
            name: '',
          },
          splashScreen: '',
          splashScreenId: 0,
          xmltvOutputURL: '',
          layout: {},
          bumper: {},
          announcement: {},
          blackout: {},
          bumperPicture: '',
          bumperPictureId: 0,
          minIntBetweenAds: 420,
          adDuration: 120,
          watermark: {
            position: 'topLeft',
            size: '200',
            enabled: false,
          },
          watermarkPicture: '',
          watermarkPictureId: 0,
        },
        watermarkPreviewCycle: true,
        watermarkBackgrounds: [
          '/images/bug_background/cartoon.jpg',
          '/images/bug_background/crime.jpg',
          '/images/bug_background/movie.jpg',
          '/images/bug_background/movie_01.jpg',
          '/images/bug_background/movie_02.jpg',
          '/images/bug_background/music.jpg',
          '/images/bug_background/nature.jpg',
          '/images/bug_background/nature_01.jpg',
          '/images/bug_background/news.jpg',
          '/images/bug_background/news_01.jpg',
          '/images/bug_background/news_02.jpg',
        ],
        iconFile: null,
        watermarkFile: null,
        bumperFile: null,
        splashScreenFile: null,
        isSaving: false,
        rules: {
          nameRules: [
            (v) => !!v || 'Name is required',
            (v) => (v && v.length <= 255) || 'Name must be less than 255 characters',
          ],
        },
        timezones: [],
        layouts: [],
        layout: '',
        bumpers: [],
        announcements: [],
        blackouts: [],
        openPlayer: false,
        player: {
          assetName: '',
          assetDescription: '',
        },
        frameSrc: '',
        layoutType: LAYOUT_TYPE,
        selectedBumper: 0,
      }
    },
    computed: {
      loading () {
        return this.$store.getters.isDataLoaded
      },
      isFormValid () {
        return this.channel.name.length >= 1 && this.channel.name.length <= 255
      },
      defaultIcon () {
        return '/images/ch_icon.png'
      },
      canNext () {
        if (this.step === 1 && !this.channel.name) {
          return false
        }
        if (!this.channel.logoId) {
          return false
        }
        return true
      },
    },
    created () {
      this.loadData()
    },
    methods: {
      loadData () {
        this.$store.commit('setPageLoader')

        const tzReq = this.$store.dispatch('timezones')
        const args = {
          'active[eq]': 1,
          $orderby: 'order',
        }
        const layoutReq = this.$store.dispatch('layouts', args)
        const templateReq = this.$store.dispatch('templates')

        return Promise.all([tzReq, layoutReq, templateReq])
          .then((res) => {
            const timezones = res[0]
            this.timezones = timezones.map((tz) => processTimezone(tz))
            for (let i = 0; i < this.timezones.length; i += 1) {
              if (this.timezones[i].name === 'UTC') {
                this.channel.timezone = this.timezones[i]
                break
              }
            }

            const layouts = res[1]
            this.layouts = layouts.map((l) => processLayout(l))

            this.layout = LAYOUT_TYPE.AD
            this.setLayout()
            if (!this.channel.layout.id && this.layouts.length > 0) {
              // eslint-disable-next-line prefer-destructuring
              this.channel.layout = this.layouts[0]
            }

            const templates = res[2].map((t) => processTemplate(t))
            if (templates) {
              for (let i = 0; i < templates.length; i += 1) {
                const t = templates[i]
                switch (t.type) {
                  case TEMPLATE_TYPE.BUMPER:
                    this.bumpers.push(t)
                    break
                  case TEMPLATE_TYPE.ANNOUNCE:
                    this.announcements.push(t)
                    break
                  case TEMPLATE_TYPE.BLACKOUT:
                    this.blackouts.push(t)
                    break
                  default:
                }
              }
            }

            if (this.bumpers.length > 0) {
              this.selectBumper(this.bumpers[0])
            }
            if (this.announcements.length > 0) {
              this.selectAnnouncement(this.announcements[0])
            }
            if (this.blackouts.length > 0) {
              this.selectBlackout(this.blackouts[0])
            }
          })
          .catch((errorMsg) => {
            this.$store.commit('showSnackbar', {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            })
          })
          .finally(() => this.$store.commit('resolvePageLoader'))
      },
      setLayout () {
        for (let i = 0; i < this.layouts.length; i += 1) {
          if (this.layouts[i].extId === this.layout) {
            this.channel.layout = this.layouts[i]
            break
          }
        }
      },
      selectBumper (bumper) {
        this.channel.bumper = bumper
      },
      selectAnnouncement (announcement) {
        this.channel.announcement = announcement
      },
      selectBlackout (blackout) {
        this.channel.blackout = blackout
      },
      prevStep () {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth',
        })
        this.step -= 1
      },
      nextStep () {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth',
        })
        this.step += 1
      },
      fileUpload (tag, fileObj) {
        if (!fileObj) return
        const file = new FormData()
        file.append('uploadfile', fileObj, fileObj.name)

        this.$store.dispatch('upload', file)
          .then((result) => {
            Vue.set(this.channel, tag, result.blob)
            Vue.set(this.channel, `${tag}Id`, result.id)
          })
          .catch((errorMsg) => {
            this.$store.commit('showSnackbar', {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            })
          })
      },
      playStreamByURL (stream, name, description, mp4 = false) {
        const link = stream.replace(/^http:\/\//, 'https://')

        this.player.assetName = name
        this.player.assetDescription = description
        if (mp4) {
          this.frameSrc = `/js/radiant/index4.html?src=${link}`
        } else {
          this.frameSrc = `/js/radiant/index2.html?src=${link}`
        }
        this.openPlayer = true
      },
      closeStream () {
        this.openPlayer = false
        this.frameSrc = '/js/radiant/index2.html?src='
      },
      save () {
        this.isSaving = true
        if (!this.isFormValid) return

        // console.log('this.channel', this.channel);

        if (this.channel.watermark.enabled) {
          this.channel.watermark.transcodingUpdateRequired = true
        }
        const channel = channelToJsonAPIChannelRequest(this.channel)

        const action = 'installChannel'
        this.$store.dispatch(action, channel)
          .then((newChannel) => {
            this.$store.commit('showSnackbar', {
              text: 'The new channel has been successfully created',
              timeout: 3000,
              color: 'success',
            })
            this.$router.push({
              name: 'DashboardPage',
              params: {
                id: newChannel.id,
              },
            })
          })
          .catch((errorMsg) => {
            this.$store.commit('showSnackbar', {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            })
          })
          // eslint-disable-next-line no-return-assign
          .finally(() => this.isSaving = false)
      },
    },
  }
</script>

<style>
  .checkboxes.v-input .v-label {
    margin-bottom: 0 !important;
  }
  .radios .v-label {
    margin: 0 !important;
  }
  .added .v-input__slot {
    padding-bottom: 0 !important;
    margin-bottom: 0 !important;
  }
  .watermark .v-snack__wrapper {
    min-width: 100px !important;
    width: 100px !important;
  }
  .selected-intro {
    border: 3px solid #45b6ff;
  }
  .selected-announcement {
    border: 3px solid orange;
  }
  .layout-table tr td {
    border-bottom: 1px solid #eee;
  }
  .layout-table .inactive {
    background-color: #FAFAFA;
    color: grey !important;
  }
  .guide-table tr td {
    background-color: #E8EAF6;
  }
  .v-window-item .v-carousel__item {
    height: 360px !important;
  }
</style>
